import {
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  Output,
} from '@angular/core';
import {
  DashboardWidget,
  WidgetLayout,
  isChromelessWidget,
  widgetDisplayTitle,
} from '../../../../../models/big-screen-dashboard.model';

/** Grid step (in viewport %) that drag and resize snap to. */
const GRID_STEP = 2.5;
/** Minimum widget size in viewport %. */
const MIN_W = 10;
const MIN_H = 8;

/**
 * Widget types whose body is a tile grid: they must never be resized so small
 * that their tiles overflow into a scroll or get clipped. The resize handler
 * clamps these up to the size their content needs.
 */
const GRID_WIDGET_TYPES: ReadonlySet<string> = new Set([
  'scanRateByEntrance',
  'errorsByResultCode',
]);

type DragMode = 'move' | 'resize';

/**
 * Positions a single dashboard widget absolutely from its percentage-based layout
 * and, in edit mode, lets the user drag it by the header and resize it from the
 * bottom-right handle. Pixel deltas are converted to viewport percentages against
 * the host's positioned parent (the widget layer) and snapped to a grid step, so a
 * layout scales proportionally across screen sizes. The new layout is emitted on
 * pointer-up for the parent to persist.
 */
@Component({
  selector: 'app-widget-host',
  standalone: false,
  templateUrl: './widget-host.component.html',
  styleUrls: ['./widget-host.component.scss'],
})
export class WidgetHostComponent {
  @Input() widget!: DashboardWidget;
  @Input() editing: boolean = false;
  @Input() dark: boolean = false;
  /** Optional custom widget-card colour; '' uses the theme's default card. */
  @Input() cardColor: string = '';

  @Output() layoutChange = new EventEmitter<WidgetLayout>();
  @Output() configure = new EventEmitter<void>();
  @Output() remove = new EventEmitter<void>();

  @HostBinding('class.editing') get isEditing(): boolean {
    return this.editing;
  }

  /**
   * Renders a widget without the card background, border and padding. Text, image
   * and venue-map widgets are always chromeless; a stat tile is chromeless only
   * when the user opts out of its background.
   */
  @HostBinding('class.chromeless') get isChromeless(): boolean {
    return !!this.widget && isChromelessWidget(this.widget);
  }

  /** Label for the edit-mode header: the widget's title or its type's default. */
  get displayTitle(): string {
    return this.widget ? widgetDisplayTitle(this.widget) : '';
  }

  private mode: DragMode | null = null;
  private startX: number = 0;
  private startY: number = 0;
  private startLayout: WidgetLayout = { x: 0, y: 0, w: 0, h: 0 };
  /** The positioned parent's pixel size, captured at drag start. */
  private parentWidth: number = 1;
  private parentHeight: number = 1;
  /** Live layout during a drag, applied to the host style before commit. */
  private liveLayout: WidgetLayout | null = null;

  constructor(private readonly host: ElementRef<HTMLElement>) {}

  @HostBinding('style.left.%') get left(): number {
    return (this.liveLayout ?? this.widget.layout).x;
  }
  @HostBinding('style.top.%') get top(): number {
    return (this.liveLayout ?? this.widget.layout).y;
  }
  @HostBinding('style.width.%') get width(): number {
    return (this.liveLayout ?? this.widget.layout).w;
  }
  @HostBinding('style.height.%') get height(): number {
    return (this.liveLayout ?? this.widget.layout).h;
  }

  onDragStart(event: PointerEvent): void {
    this.beginPointer(event, 'move');
  }

  onResizeStart(event: PointerEvent): void {
    event.stopPropagation();
    this.beginPointer(event, 'resize');
  }

  private beginPointer(event: PointerEvent, mode: DragMode): void {
    if (!this.editing) return;
    event.preventDefault();
    const parent = this.host.nativeElement.parentElement;
    const rect = parent?.getBoundingClientRect();
    this.parentWidth = rect?.width || 1;
    this.parentHeight = rect?.height || 1;
    this.mode = mode;
    this.startX = event.clientX;
    this.startY = event.clientY;
    this.startLayout = { ...this.widget.layout };
    this.liveLayout = { ...this.widget.layout };
    (event.target as HTMLElement).setPointerCapture?.(event.pointerId);
  }

  onPointerMove(event: PointerEvent): void {
    if (!this.mode || !this.liveLayout) return;
    const dxPct = ((event.clientX - this.startX) / this.parentWidth) * 100;
    const dyPct = ((event.clientY - this.startY) / this.parentHeight) * 100;

    if (this.mode === 'move') {
      this.liveLayout = {
        ...this.startLayout,
        x: this.clamp(this.startLayout.x + dxPct, 0, 100 - this.startLayout.w),
        y: this.clamp(this.startLayout.y + dyPct, 0, 100 - this.startLayout.h),
      };
    } else {
      this.liveLayout = {
        ...this.startLayout,
        w: this.clamp(this.startLayout.w + dxPct, MIN_W, 100 - this.startLayout.x),
        h: this.clamp(this.startLayout.h + dyPct, MIN_H, 100 - this.startLayout.y),
      };
    }
  }

  onPointerUp(event: PointerEvent): void {
    if (!this.mode || !this.liveLayout) return;
    (event.target as HTMLElement).releasePointerCapture?.(event.pointerId);
    // Snap to the grid on commit, then clamp so snapping can't push out of bounds.
    const snapped: WidgetLayout = {
      x: this.snap(this.liveLayout.x),
      y: this.snap(this.liveLayout.y),
      w: this.snap(this.liveLayout.w),
      h: this.snap(this.liveLayout.h),
    };
    snapped.w = this.clamp(snapped.w, MIN_W, 100 - snapped.x);
    snapped.h = this.clamp(snapped.h, MIN_H, 100 - snapped.y);
    // Grid widgets must show every tile without an internal scroll; grow the
    // committed height to fit the content measured at the just-resized size.
    if (this.mode === 'resize' && GRID_WIDGET_TYPES.has(this.widget.type)) {
      snapped.h = this.fitGridHeight(snapped);
    }
    this.mode = null;
    this.liveLayout = null;
    this.layoutChange.emit(snapped);
  }

  /**
   * Returns a height (viewport %) large enough that the widget's tile grid shows
   * all tiles without an internal scroll. Because the tiles' text scales with the
   * widget size (container queries), growing the height also grows the tiles, so a
   * single measurement can undershoot. This grows the host in a few passes: each
   * pass sets the host's inline height, forces a reflow by reading the grid's
   * overflow, and adds it back until the grid fits or the bottom edge is reached.
   * The temporary inline height is cleared afterwards so the @HostBinding wins.
   */
  private fitGridHeight(layout: WidgetLayout): number {
    const el = this.host.nativeElement;
    const grid = el.querySelector<HTMLElement>('.modern-rate-grid, .modern-error-grid');
    const maxH = 100 - layout.y;
    if (!grid || this.parentHeight <= 0) return layout.h;

    let height = layout.h;
    try {
      for (let pass = 0; pass < 4 && height < maxH; pass++) {
        el.style.height = `${height}%`;
        // Reading a layout property forces the browser to reflow at the new size.
        const overflowPx = grid.scrollHeight - grid.clientHeight;
        if (overflowPx <= 0) break;
        const overflowPct = (overflowPx / this.parentHeight) * 100;
        height = this.clamp(this.snap(height + overflowPct), MIN_H, maxH);
      }
    } finally {
      // Hand sizing back to the @HostBinding; Angular re-applies it on the next
      // change-detection pass from the emitted layout.
      el.style.height = '';
    }
    return height;
  }

  private snap(value: number): number {
    return Math.round(value / GRID_STEP) * GRID_STEP;
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), Math.max(min, max));
  }
}
